import OpenPanelFilledLeft20 from '@carbon/icons-react/es/open-panel--filled--left/20';
import { Editor } from '@ckeditor/ckeditor5-core/src/editor/editor';
import { Tooltip } from 'antd';
import classNames from 'classnames';
import React, { FC, ReactElement, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { StyledSidebarToggleButton } from 'components/organisms/spaces/space-tab/styles';
import { useDistractionFreeMode } from 'hooks/use-distraction-free-mode';
import { PrereleaseFeature, useFeature } from 'hooks/use-feature';
import { useTranslation } from 'hooks/use-translation';
import styles from './styles.module.css';
import { DocumentToolbar } from './toolbar';
import { appToolGroupNames, tools as defaultTools } from './tools';
import { Tools } from './types.d';

interface Props {
  editor: Editor;
  showDocTitle: boolean;
  docTitle: string;
  topbarClassName?: string;
  readOnly?: boolean;
  hideTools?: boolean;
  renderExtraFields?: () => ReactElement;
  tools?: Tools;
  isResponsive?: boolean;
}

export const Topbar: FC<Props> = ({
  editor,
  showDocTitle,
  docTitle,
  topbarClassName,
  readOnly,
  hideTools,
  renderExtraFields,
  tools = defaultTools,
  isResponsive = false,
}: Props) => {
  const [isVisible, setVisibility] = useState(false);

  const showToolbar = () => {
    setVisibility(true);
  };

  const { active: isDistractionFree, enter, exit } = useDistractionFreeMode();

  function onSidebarToggle() {
    if (isDistractionFree) {
      exit();
    } else {
      enter();
    }
  }

  // Commenting out the toolbar fade part as fading makes layout weird now with comment pane now.
  //  const hideToolbar = () => {
  //    setVisibility(false);
  //  };
  //  let time;
  // This function keep a memoised reference to resetTimer, which throttles mouse events every
  // 500ms and keep the toolbar visible when mouse is moving.
  //  const resetTimer = useCallback(
  //    throttle(
  //      () => {
  //        showToolbar();
  //        clearTimeout(time);
  //        time = window.setTimeout(hideToolbar, 3000);
  //      },
  //      500,
  //      {
  //        leading: true,
  //        trailing: false,
  //      },
  //    ),
  //    [],
  //  );
  useEffect(() => {
    if (editor !== null) {
      //  document.body.addEventListener('mousemove', resetTimer);
      //  resetTimer();
      showToolbar();
    }
    //  return document.removeEventListener('mousemove', resetTimer);
  }, [editor]);
  const docTitleClasses = classNames(styles.doc__title, {
    [styles.doc__title_visible]: showDocTitle,
  });
  const toolbarClasses = classNames(styles.topbar, topbarClassName);

  const toolsClasses = classNames({
    [styles.visible]: isVisible && !readOnly,
  });

  const sidebarCollapseText = useTranslation('collapse_sidebar');

  const { pathname } = useLocation();
  const isTemplateDoc =
    pathname.includes('templates') || pathname.includes('status-updates');
  const meetingsAIEnabled = useFeature(PrereleaseFeature.MeetingsAI);
  const headerTools = useMemo(
    () =>
      meetingsAIEnabled
        ? tools
        : tools.filter(({ group }) => !appToolGroupNames.includes(group)),
    [meetingsAIEnabled, tools],
  );

  return (
    <div data-cy="spaces-toolbar">
      <div className={docTitleClasses} key="title">
        {docTitle}
      </div>
      <div key="toolbar" className={toolbarClasses}>
        {!(isTemplateDoc || isResponsive) && (
          <Tooltip title={sidebarCollapseText} trigger={['hover']}>
            <StyledSidebarToggleButton
              type="text"
              size="small"
              onClick={onSidebarToggle}
            >
              <OpenPanelFilledLeft20 color="var(--blue-60)" />
            </StyledSidebarToggleButton>
          </Tooltip>
        )}
        {!(hideTools || isResponsive) ? (
          <DocumentToolbar
            editor={editor}
            className={toolsClasses}
            tools={headerTools}
          />
        ) : null}
        {/* <DocumentActions /> */}
        {renderExtraFields ? (
          <div className={styles['extra-fields']}>{renderExtraFields()}</div>
        ) : null}
      </div>
    </div>
  );
};
